import React from 'react';
import styles from './FilterBar.module.css';

export interface FilterFieldOption {
  value: string;
  label: string;
}

export interface FilterFieldConfig {
  key: string;
  type: 'select' | 'date' | 'text';
  label: string;
  placeholder?: string;
  options?: FilterFieldOption[];
}

interface FilterBarProps {
  fields: FilterFieldConfig[];
  values: Record<string, string>;
  onChange: (key: string, value: string) => void;
  onClear?: () => void;
  /** Extra controls rendered on the right (e.g. an Export button). */
  actions?: React.ReactNode;
}

/**
 * Shared filter bar (Sec 12) modeled on
 * src/pages/callcenter/AllTickets/components/FilterBar.tsx — a small,
 * generic, config-driven version of the same visual pattern so report pages
 * (Accounting, Sales, ...) don't each hand-roll their own filter row.
 */
export const FilterBar: React.FC<FilterBarProps> = ({ fields, values, onChange, onClear, actions }) => {
  const hasActiveFilter = fields.some((f) => (values[f.key] ?? '') !== '');

  return (
    <div className={styles.bar}>
      <div className={styles.filters}>
        {fields.map((field) => {
          const value = values[field.key] ?? '';
          if (field.type === 'select') {
            return (
              <select
                key={field.key}
                className={styles.filter}
                value={value}
                onChange={(e) => onChange(field.key, e.target.value)}
                aria-label={field.label}
              >
                <option value="">{field.label}</option>
                {(field.options ?? []).map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            );
          }
          if (field.type === 'date') {
            return (
              <input
                key={field.key}
                type="date"
                className={styles.filter}
                value={value}
                onChange={(e) => onChange(field.key, e.target.value)}
                title={field.label}
              />
            );
          }
          return (
            <input
              key={field.key}
              type="text"
              className={styles.filter}
              placeholder={field.placeholder ?? field.label}
              value={value}
              onChange={(e) => onChange(field.key, e.target.value)}
              title={field.label}
            />
          );
        })}
        {onClear && hasActiveFilter && (
          <button type="button" className={styles.clearBtn} onClick={onClear}>
            Clear filters
          </button>
        )}
      </div>
      {actions && <div className={styles.actions}>{actions}</div>}
    </div>
  );
};
